/* ============================================================================
   pdp-reviews.js — hydratisiert den theme-eigenen Bewertungs-Container
   (snippets/pdp-reviews.liquid) mit Judge.me-Daten in UNSER Markup.

   Architektur (docs/17-judgeme-reviews.md):
     • Die Summary (Schnitt + Anzahl) rendert bereits server-seitig aus den
       reviews.* Metafeldern — dieses Script ergänzt NUR Histogramm, Liste,
       Foto-Strip, Händler-Antworten und KI-Zusammenfassung.
     • Datenquelle über EINEN isolierten Adapter `loadModel()`:
         1. Fixtures  (data-rv-fixtures, Demo-/QA-Modus) — voll funktionsfähig.
         2. Judge.me Cache-Server (Production, Awesome) — JSON ODER Widget-HTML;
            `normalize()` toleriert beide Formen. ⚠ Erst nach Awesome + Public-Token
            gegen echte Antwort verifizieren (eine Funktion, klar isoliert).
     • Fällt etwas aus → Skeletons weg, Summary bleibt, dezenter Status. Nie kaputt.
   ========================================================================== */
(function () {
  'use strict';

  var AVATAR_PALETTE = [
    { bg: 'var(--door-tint)', fg: 'var(--door-cta)' },
    { bg: 'var(--climate-tint)', fg: 'var(--climate-cta)' },
    { bg: 'var(--garden-tint)', fg: 'var(--garden-cta)' },
    { bg: 'var(--multimedia-tint)', fg: 'var(--multimedia-cta)' },
    { bg: 'var(--health-tint)', fg: 'var(--health-cta)' }
  ];
  var PHOTO_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="m21 16-5-5L5 20"/></svg>';
  var HOUSE_ICON = '<svg class="hm-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 11l9-8 9 8M5 10v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V10"/></svg>';
  var CHECK_ICON = '<svg class="hm-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
  // Quellen-Label-Fallback, wenn ein Set/Produkt kein Bild hat (kleine „Paket"-Silhouette)
  var BOX_ICON = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 8 12 3 3 8v8l9 5 9-5z"/><path d="M3 8l9 5 9-5M12 13v8"/></svg>';

  // I18N lebt pro Container in cfg.i18n (init() liest [data-rv-i18n]) — kein Modul-Global,
  // damit mehrere [data-reviews]-Container auf einer Seite sich die Texte nicht überschreiben.
  function L(i18n, key, fallback) { return (i18n && i18n[key] != null && i18n[key] !== '') ? i18n[key] : fallback; }
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function stars(rating) {
    var r = Math.round(rating || 0), s = '';
    for (var i = 1; i <= 5; i++) s += i <= r ? '★' : '☆';
    return s;
  }
  function initials(name) {
    var parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '–';
    var a = parts[0][0] || '';
    var b = parts.length > 1 ? (parts[parts.length - 1][0] || '') : '';
    return (a + b).toUpperCase();
  }
  /* erlaubt nur <b>/<strong> in der KI-Zusammenfassung, escapt den Rest (XSS-sicher) */
  function safeEmphasis(str) {
    var esc = String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return esc.replace(/&lt;(\/?)(b|strong)&gt;/gi, '<$1$2>');
  }

  /* ---------------- Adapter: liefert ein normalisiertes Modell ---------------- */
  function readFixtures(root) {
    var s = root.querySelector('[data-rv-fixtures]');
    if (!s) return null;
    try { return normalizeModel(JSON.parse(s.textContent)); } catch (e) { return null; }
  }

  // Ein Produkt laden. Production: Judge.me Product-Review-Widget-API (öffentlicher Token, CORS-fähig,
  // Free-Plan). Antwort ist JSON { product_external_id, widget }, wobei `widget` das gerenderte jdgm-
  // Widget-HTML ist (KEIN sauberes Review-JSON). Darum als Text lesen, JSON parsen, dann normalize() →
  // findWidgetHtml()/parseWidgetHtml() den HTML-Teil extrahieren lassen. Produkt via handle (Fallback
  // external_id). Verifiziert 2026-07: cache.judge.me/widgets/… liefert „Shop not found".
  function fetchOne(cfg, ident) {
    var handle = ident && ident.handle;
    var id = ident && ident.id;
    if (!cfg.token || !cfg.shopDomain || (!handle && !id)) return Promise.resolve(null);
    var q = handle
      ? '&handle=' + encodeURIComponent(handle)
      : '&external_id=' + encodeURIComponent(id);
    var url = 'https://judge.me/api/v1/widgets/product_review' +
      '?api_token=' + encodeURIComponent(cfg.token) +
      '&shop_domain=' + encodeURIComponent(cfg.shopDomain) +
      q + '&per_page=50';
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('jdgm ' + r.status); return r.text(); })
      .then(function (text) {
        var data;
        try { data = JSON.parse(text); } catch (e) { data = text; }
        return normalize(data, cfg);
      });
  }

  // Bundle-PDP: mehrere Quellen (Set + enthaltene Produkte) parallel laden, jede Review mit ihrer Quelle
  // taggen (Label an der Karte), zusammenführen und nach Datum absteigend sortieren. Fällt eine Quelle aus,
  // wird sie übersprungen (leere Liste) — die übrigen Reviews erscheinen trotzdem.
  function loadMulti(cfg) {
    var jobs = cfg.sources.map(function (src) {
      return fetchOne(cfg, { handle: src.handle, id: src.id })
        .then(function (model) {
          var revs = (model && model.reviews) ? model.reviews : [];
          revs.forEach(function (r) { r.source = src.title; r.sourceImage = src.image || ''; });
          return revs;
        })
        .catch(function () { return []; });
    });
    return Promise.all(jobs).then(function (lists) {
      var all = [];
      lists.forEach(function (l) { all = all.concat(l); });
      all.sort(function (a, b) { return (b.ts || 0) - (a.ts || 0); });
      return normalizeModel({ reviews: all, histogram: histogramFromReviews(all) });
    });
  }

  function loadModel(root, cfg) {
    var fx = readFixtures(root);
    if (fx) return Promise.resolve(fx);
    if (cfg.sources && cfg.sources.length) return loadMulti(cfg);
    return fetchOne(cfg, { handle: cfg.productHandle, id: cfg.productId });
  }

  /* Toleriert (a) rohen HTML-String, (b) strukturierte Review-Objekte oder (c) verschachteltes jdgm-Widget-HTML. */
  function normalize(data, cfg) {
    if (!data) return null;
    // (roh) String = direkt geliefertes Widget-HTML/JS → jdgm-Markup extrahieren
    if (typeof data === 'string') {
      return data.indexOf('jdgm-rev') > -1 ? parseWidgetHtml(data, cfg.i18n) : null;
    }
    // (a) irgendwo ein Array review-artiger Objekte?
    var arr = findReviewArray(data);
    if (arr && arr.length) {
      var mapped = arr.map(function (r) { return mapApiReview(r, cfg.i18n); });
      return normalizeModel({
        reviews: mapped,
        histogram: histogramFromReviews(mapped)
      });
    }
    // (b) Widget-HTML extrahieren
    var html = findWidgetHtml(data, cfg);
    if (html) return parseWidgetHtml(html, cfg.i18n);
    return null;
  }

  function findReviewArray(data) {
    if (Array.isArray(data.reviews)) return data.reviews;
    var keys = Object.keys(data || {}), i, v;
    for (i = 0; i < keys.length; i++) {
      v = data[keys[i]];
      if (Array.isArray(v) && v.length && v[0] && typeof v[0] === 'object' && ('rating' in v[0] || 'body' in v[0])) return v;
      if (v && typeof v === 'object' && Array.isArray(v.reviews)) return v.reviews;
    }
    return null;
  }
  function findWidgetHtml(data, cfg) {
    var stack = [data], seen = 0;
    while (stack.length && seen < 400) {
      var cur = stack.pop(); seen++;
      if (typeof cur === 'string') { if (cur.indexOf('jdgm-rev') > -1) return cur; continue; }
      if (cur && typeof cur === 'object') for (var k in cur) if (Object.prototype.hasOwnProperty.call(cur, k)) stack.push(cur[k]);
    }
    return null;
  }
  function mapApiReview(r, i18n) {
    return {
      name: r.reviewer && r.reviewer.name ? r.reviewer.name : (r.reviewer_name || r.name || L(i18n, 'anonymous', 'Anonym')),
      rating: Number(r.rating) || 0,
      verified: r.verified === 'buyer' || r.verified === true,
      date: r.created_at ? relTime(r.created_at, i18n) : (r.date || ''),
      ts: toTs(r.created_at),
      title: r.title || '',
      body: r.body || '',
      photos: (r.pictures || r.photos || []).map(function (p) {
        return { url: p && p.urls ? (p.urls.compact || p.urls.small || p.urls.original) : (p && p.url) || '' };
      }),
      reply: r.reply ? { text: typeof r.reply === 'string' ? r.reply : (r.reply.body || r.reply.text || '') } : null
    };
  }
  function parseWidgetHtml(html, i18n) {
    var doc;
    try { doc = new DOMParser().parseFromString(html, 'text/html'); } catch (e) { return null; }
    // Feld-Mapping gegen das ECHTE jdgm-Widget-Markup (verifiziert 2026-07 am Live-Response):
    //   Rating   → .jdgm-rev__rating[data-score]   (Fallback: Anzahl .jdgm-star.jdgm--on)
    //   Datum    → .jdgm-rev__timestamp[data-content] (Text ist leer, füllt sonst Judge.me-JS)
    //   Verifiz. → .jdgm-rev[data-verified-buyer='true']
    //   Antwort  → .jdgm-rev__reply-content
    var reviews = [].map.call(doc.querySelectorAll('.jdgm-rev'), function (n) {
      var ratingEl = n.querySelector('.jdgm-rev__rating');
      var rating = ratingEl && ratingEl.getAttribute('data-score')
        ? Number(ratingEl.getAttribute('data-score'))
        : (n.querySelectorAll('.jdgm-star.jdgm--on').length || 0);
      var tsEl = n.querySelector('.jdgm-rev__timestamp');
      var tsRaw = tsEl ? (tsEl.getAttribute('data-content') || tsEl.textContent || '') : '';
      var replyEl = n.querySelector('.jdgm-rev__reply');
      var replyText = replyEl ? (txt(replyEl, '.jdgm-rev__reply-content') || '').trim() : '';
      return {
        name: txt(n, '.jdgm-rev__author') || L(i18n, 'anonymous', 'Anonym'),
        rating: rating,
        verified: n.getAttribute('data-verified-buyer') === 'true' || !!n.querySelector('.jdgm-rev__buyer-badge'),
        date: relTime(tsRaw, i18n) || tsRaw,
        ts: toTs(tsRaw),
        title: txt(n, '.jdgm-rev__title'),
        body: txt(n, '.jdgm-rev__body'),
        photos: [].map.call(n.querySelectorAll('.jdgm-rev__pic img, .jdgm-rev__pics img'), function (im) { return { url: im.getAttribute('data-src') || im.src }; }),
        reply: replyText ? { text: replyText } : null
      };
    });
    var hist = [].map.call(doc.querySelectorAll('.jdgm-histogram__row'), function (row) {
      return { star: Number(row.getAttribute('data-rating')), pct: Number(row.getAttribute('data-percentage')) || 0 };
    }).filter(function (h) { return h.star >= 1 && h.star <= 5; });
    return normalizeModel({ reviews: reviews, histogram: hist.length ? hist : histogramFromReviews(reviews) });
  }
  function txt(scope, sel) { var n = scope.querySelector(sel); return n ? n.textContent.trim() : ''; }

  function histogramFromReviews(reviews) {
    var buckets = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, total = 0;
    reviews.forEach(function (r) { var k = Math.round(r.rating); if (buckets[k] != null) { buckets[k]++; total++; } });
    return [5, 4, 3, 2, 1].map(function (s) { return { star: s, pct: total ? Math.round((buckets[s] / total) * 100) : 0 }; });
  }
  // Epoch-ms aus einem Judge.me-/ISO-Datum (für die Sortierung der gemischten Bundle-Liste). 0 = unbekannt.
  function toTs(iso) {
    if (!iso) return 0;
    var d = new Date(String(iso).replace(' UTC', 'Z').replace(' ', 'T'));
    return isNaN(d) ? 0 : d.getTime();
  }
  function relTime(iso, i18n) {
    if (!iso) return '';
    // Judge.me liefert z. B. „2026-06-11 22:00:00 UTC" → in parsebares ISO wandeln.
    var d = new Date(String(iso).replace(' UTC', 'Z').replace(' ', 'T'));
    if (isNaN(d)) return '';
    var days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days <= 0) return L(i18n, 'dateToday', 'Heute');
    if (days === 1) return L(i18n, 'dateYesterday', 'Gestern');
    if (days < 14) return L(i18n, 'dateDays', 'Vor %n% Tagen').replace('%n%', days);
    if (days < 60) return L(i18n, 'dateWeeks', 'Vor %n% Wochen').replace('%n%', Math.floor(days / 7));
    return L(i18n, 'dateMonths', 'Vor %n% Monaten').replace('%n%', Math.floor(days / 30));
  }
  function normalizeModel(m) {
    m = m || {};
    m.reviews = Array.isArray(m.reviews) ? m.reviews : [];
    if (!Array.isArray(m.histogram) || !m.histogram.length) m.histogram = histogramFromReviews(m.reviews);
    return m;
  }

  /* ---------------------------- Rendering ---------------------------- */
  function buildCard(r, idx, i18n) {
    var card = el('li', 'hm-reviews__card');

    // Quellen-Label (nur Bundle): auf welches Set/Produkt sich die Review bezieht. r.source wird nur
    // im Bundle-Modus gesetzt (loadMulti-Tagging oder Bundle-Fixtures) → auf der Einzel-PDP kein Label.
    if (r.source) {
      var srcTag = el('div', 'hm-reviews__src');
      srcTag.title = r.source;
      srcTag.appendChild(el('span', 'visually-hidden', L(i18n, 'sourcePrefix', 'Bewertung zu') + ' '));
      if (r.sourceImage) {
        var sim = new Image(); sim.className = 'hm-reviews__src-thumb'; sim.loading = 'lazy'; sim.src = r.sourceImage; sim.alt = '';
        srcTag.appendChild(sim);
      } else {
        var sic = el('span', 'hm-reviews__src-ic'); sic.innerHTML = BOX_ICON; sic.setAttribute('aria-hidden', 'true');
        srcTag.appendChild(sic);
      }
      srcTag.appendChild(el('span', 'hm-reviews__src-txt', r.source));
      card.appendChild(srcTag);
    }

    var top = el('div', 'hm-reviews__card-top');

    var author = el('div', 'hm-reviews__author');
    var pal = AVATAR_PALETTE[idx % AVATAR_PALETTE.length];
    var av = el('span', 'hm-reviews__avatar', initials(r.name));
    av.style.setProperty('--av-bg', pal.bg); av.style.setProperty('--av-fg', pal.fg);
    av.setAttribute('aria-hidden', 'true');
    var who = el('div', 'hm-reviews__who');
    who.appendChild(el('span', 'hm-reviews__name', r.name));
    if (r.verified) {
      var vb = el('span', 'hm-reviews__verified');
      vb.innerHTML = CHECK_ICON;
      vb.appendChild(document.createTextNode(' ' + L(i18n, 'verified', 'Verifizierter Kauf')));
      who.appendChild(vb);
    }
    author.appendChild(av); author.appendChild(who);

    var cs = el('span', 'hm-reviews__card-stars', stars(r.rating));
    cs.setAttribute('aria-label', L(i18n, 'starsOf5', '%n% von 5 Sternen').replace('%n%', Math.round(r.rating)));
    top.appendChild(author); top.appendChild(cs);
    card.appendChild(top);

    if (r.title) card.appendChild(el('h3', 'hm-reviews__card-title', r.title));
    if (r.body) card.appendChild(el('p', 'hm-reviews__card-body', r.body));

    var pics = (r.photos || []).filter(Boolean);
    if (pics.length) {
      var pr = el('div', 'hm-reviews__card-photos');
      pics.slice(0, 4).forEach(function (ph) { pr.appendChild(buildThumb(ph, i18n)); });
      card.appendChild(pr);
    }

    if (r.reply && r.reply.text) {
      var rep = el('div', 'hm-reviews__reply');
      var rh = el('div', 'hm-reviews__reply-head');
      rh.innerHTML = HOUSE_ICON;
      rh.appendChild(document.createTextNode(' ' + L(i18n, 'replyFrom', 'Antwort von Homedia')));
      rep.appendChild(rh);
      rep.appendChild(el('p', 'hm-reviews__reply-text', r.reply.text));
      card.appendChild(rep);
    }

    var foot = el('p', 'hm-reviews__card-foot');
    if (r.date) foot.appendChild(el('span', 'hm-reviews__foot-date', r.date + ' · '));
    foot.appendChild(el('span', 'hm-reviews__foot-q', L(i18n, 'helpfulQ', 'War das hilfreich?') + ' '));
    var help = el('button', 'hm-reviews__helpful');
    help.type = 'button';
    var yes = L(i18n, 'helpfulYes', 'Ja');
    var votes = 0;
    help.textContent = yes;
    help.setAttribute('aria-pressed', 'false');
    help.addEventListener('click', function () {
      var on = help.getAttribute('aria-pressed') === 'true';
      votes += on ? -1 : 1;
      help.setAttribute('aria-pressed', on ? 'false' : 'true');
      help.textContent = yes + (votes > 0 ? ' (' + votes + ')' : '');
    });
    foot.appendChild(help);
    card.appendChild(foot);
    return card;
  }
  function buildThumb(ph, i18n) {
    var b = el('button', 'hm-reviews__pthumb'); b.type = 'button';
    b.setAttribute('aria-label', L(i18n, 'photoView', 'Bewertungsfoto ansehen'));
    if (ph && ph.url) {
      var im = new Image(); im.loading = 'lazy'; im.src = ph.url; im.alt = '';
      b.appendChild(im);
    } else {
      b.innerHTML = PHOTO_ICON;
    }
    return b;
  }

  function renderHistogram(root, hist) {
    (hist || []).forEach(function (h) {
      var row = root.querySelector('[data-rv-hist-row="' + h.star + '"]');
      if (!row) return;
      var fill = row.querySelector('.hm-reviews__hist-fill');
      var pct = row.querySelector('[data-rv-hist-pct]');
      var v = Math.max(0, Math.min(100, Math.round(h.pct)));
      if (fill) fill.style.width = v + '%';
      if (pct) pct.textContent = v + '%';
    });
  }
  function renderPhotos(root, model, i18n) {
    var wrap = root.querySelector('[data-rv-photos]');
    var strip = root.querySelector('[data-rv-photos-strip]');
    if (!wrap || !strip) return;
    var all = [];
    model.reviews.forEach(function (r) { (r.photos || []).forEach(function (p) { all.push(p); }); });
    if (!all.length) return;
    var total = model.photos_total || all.length;
    var label = root.querySelector('[data-rv-photos-label]');
    if (label) label.textContent = L(i18n, 'photosLabel', 'Fotos aus Bewertungen (%n%)').replace('%n%', total);
    all.slice(0, 12).forEach(function (p) { strip.appendChild(buildThumb(p, i18n)); });
    wrap.hidden = false;
  }
  function renderAi(root, ai) {
    if (!ai || !ai.text) return;
    var box = root.querySelector('[data-rv-ai]');
    if (!box) return;
    var txtEl = box.querySelector('[data-rv-ai-text]');
    if (txtEl) txtEl.innerHTML = safeEmphasis(ai.text);
    var chips = box.querySelector('[data-rv-ai-chips]');
    if (chips) {
      (ai.positives || []).forEach(function (c) { chips.appendChild(chip('pos', '+ ', c)); });
      (ai.negatives || []).forEach(function (c) { chips.appendChild(chip('neg', '− ', c)); });
    }
    var foot = box.querySelector('[data-rv-ai-foot]');
    if (foot && ai.footnote) { foot.textContent = ai.footnote; foot.hidden = false; }
    box.hidden = false;
  }
  function chip(kind, prefix, c) {
    var label = Array.isArray(c) ? c[0] + (c[1] != null ? ' (' + c[1] + ')' : '') : String(c);
    return el('span', 'hm-reviews__ai-chip hm-reviews__ai-chip--' + kind, prefix + label);
  }

  /* ------------------------- List + Filter + Paginierung ------------------------- */
  function setupList(root, model, cfg) {
    var listEl = root.querySelector('[data-rv-list]');
    var moreBtn = root.querySelector('[data-rv-more]');
    var status = root.querySelector('[data-rv-status]');
    // Mobile (≤749px): standardmäßig nur eine Bewertung sichtbar (spart Platz), der Rest
    // hängt hinter dem „Mehr"-Button. Desktop startet weiterhin mit der vollen Seitengröße.
    var isMobileView = !!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
    var initialShown = isMobileView ? cfg.mobileInitial : cfg.pageSize;
    var state = { filter: 'all', shown: initialShown };

    function filtered() {
      if (state.filter === 'photos') return model.reviews.filter(function (r) { return (r.photos || []).length; });
      if (state.filter === 'critical') return model.reviews.filter(function (r) { return r.rating <= 3; });
      return model.reviews;
    }
    function render(focusNew) {
      var data = filtered();
      var firstNewIdx = focusNew ? Math.min(state.shown - cfg.pageSize, data.length) : -1;
      listEl.innerHTML = '';
      var slice = data.slice(0, state.shown);
      slice.forEach(function (r, i) { listEl.appendChild(buildCard(r, i, cfg.i18n)); });
      if (!slice.length) {
        listEl.appendChild(el('li', 'hm-reviews__card', L(cfg.i18n, 'noneFilter', 'Keine Bewertungen für diesen Filter.')));
      }
      // „Mehr"-Button
      var totalCount = cfg.count || data.length;
      if (state.shown < data.length) {
        moreBtn.hidden = false;
        moreBtn.textContent = L(cfg.i18n, 'showAll', 'Alle %n% Bewertungen anzeigen').replace('%n%', totalCount);
      } else {
        moreBtn.hidden = true;
      }
      if (status) status.textContent = '';
      if (focusNew && firstNewIdx >= 0 && listEl.children[firstNewIdx]) {
        var h = listEl.children[firstNewIdx].querySelector('.hm-reviews__card-title') || listEl.children[firstNewIdx];
        h.setAttribute('tabindex', '-1'); h.focus();
      }
    }

    moreBtn.addEventListener('click', function () {
      state.shown += cfg.pageSize;
      render(true);
    });

    var pills = root.querySelectorAll('[data-rv-filter]');
    [].forEach.call(pills, function (pill) {
      pill.addEventListener('click', function () {
        state.filter = pill.getAttribute('data-rv-filter');
        state.shown = initialShown;
        [].forEach.call(pills, function (p) {
          var active = p === pill;
          p.classList.toggle('is-active', active);
          p.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        render(false);
      });
    });

    render(false);
  }

  /* ------------------------------ Init ------------------------------ */
  function setupWrite(root, cfg) {
    var btn = root.querySelector('[data-rv-write]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      // Eigenes Markup → Einreichung läuft über Judge.mes eigenes Schreib-Formular (Modal inkl.
      // E-Mail-Verifizierung), das aus dem versteckten .jdgm-review-widget-Mount stammt. Event für
      // spätere Hooks auslösen, dann den .jdgm-write-rev-link klicken. Judge.mes JS hydriert evtl.
      // asynchron → kurz nachfassen, falls der Link beim ersten Klick noch nicht existiert.
      root.dispatchEvent(new CustomEvent('hm:review-write', { bubbles: true, detail: { productId: cfg.productId } }));
      var link = document.querySelector('.jdgm-write-rev-link');
      if (link) { link.click(); return; }
      var tries = 0;
      var iv = setInterval(function () {
        var l = document.querySelector('.jdgm-write-rev-link');
        if (l) { clearInterval(iv); l.click(); }
        else if (++tries > 20) { clearInterval(iv); }   // ~5 s warten, dann aufgeben
      }, 250);
    });
  }

  function clearSkeletons(root) {
    [].forEach.call(root.querySelectorAll('.hm-reviews__card--skeleton'), function (n) { n.remove(); });
  }

  function init(root) {
    if (root.__hmRvInit) return; root.__hmRvInit = true;
    var i18n = {};
    var i18nEl = root.querySelector('[data-rv-i18n]');
    if (i18nEl) { try { i18n = JSON.parse(i18nEl.textContent) || {}; } catch (e) { i18n = {}; } }
    // Bundle-Quellen (Set + enthaltene Produkte) — nur auf der Bundle-PDP vorhanden.
    var sources = [];
    var srcEl = root.querySelector('[data-rv-sources]');
    if (srcEl) { try { sources = JSON.parse(srcEl.textContent) || []; } catch (e) { sources = []; } }
    var cfg = {
      i18n: i18n,
      productId: root.getAttribute('data-product-id'),
      productHandle: root.getAttribute('data-product-handle'),
      shopDomain: root.getAttribute('data-shop-domain'),
      token: root.getAttribute('data-token'),
      count: parseInt(root.getAttribute('data-count'), 10) || 0,
      pageSize: parseInt(root.getAttribute('data-page-size'), 10) || 5,
      mobileInitial: parseInt(root.getAttribute('data-page-size-mobile'), 10) || 1,
      sources: Array.isArray(sources) ? sources : []
    };
    setupWrite(root, cfg);
    var status = root.querySelector('[data-rv-status]');

    loadModel(root, cfg).then(function (model) {
      clearSkeletons(root);
      if (!model || !model.reviews.length) {
        // Summary bleibt server-seitig stehen; Liste konnte nicht geladen werden.
        if (status) status.textContent = L(cfg.i18n, 'loading', 'Bewertungen werden in Kürze geladen.');
        var more = root.querySelector('[data-rv-more]'); if (more) more.hidden = true;
        return;
      }
      renderHistogram(root, model.histogram);
      renderPhotos(root, model, cfg.i18n);
      renderAi(root, model.ai);
      setupList(root, model, cfg);
    }).catch(function (err) {
      clearSkeletons(root);
      if (status) status.textContent = L(cfg.i18n, 'error', 'Bewertungen konnten nicht geladen werden.');
      if (window && window.console) console.warn('[pdp-reviews]', err);
    });
  }

  function boot() {
    [].forEach.call(document.querySelectorAll('[data-reviews]'), init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // Theme-Editor: Block neu geladen
  document.addEventListener('shopify:section:load', boot);
})();
